import React, { useEffect, useState } from 'react';
import {
  Card, Button, Tree, Modal, Form, Input, InputNumber, Typography, App, Popconfirm,
  Space, Tag, Tooltip, Spin, Empty, Row, Col, Divider, Badge, Select
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, RobotOutlined,
  FolderOutlined, FileImageOutlined, AppstoreOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import api from '@/api/client';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface Phase {
  id: number;
  name: string;
  description?: string;
  sort_order: number;
}

interface Group {
  id: number;
  phase_id: number;
  name: string;
  description?: string;
  prompt?: string;
  sort_order: number;
}

interface Chart {
  id: number;
  group_id: number;
  name: string;
  chart_type: string;
  description?: string;
  prompt?: string;
  sort_order: number;
}

interface TreeData {
  phases: Phase[];
  groups: Group[];
  charts: Chart[];
}

interface TreeNode {
  key: string;
  title: React.ReactNode;
  children?: TreeNode[];
  isLeaf?: boolean;
  data?: Phase | Group | Chart;
  type: 'phase' | 'group' | 'chart';
}

export default function ScenesPage() {
  const [treeData, setTreeData] = useState<TreeData>({ phases: [], groups: [], charts: [] });
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [modal, setModal] = useState<{ open: boolean; type: 'phase' | 'group' | 'chart'; editData?: any; parentId?: number }>({ open: false, type: 'phase' });
  const [aiModal, setAiModal] = useState<{ open: boolean; generating: boolean; description: string }>({ open: false, generating: false, description: '' });
  const [form] = Form.useForm();
  const [aiForm] = Form.useForm();
  const { message } = App.useApp();

  const loadTree = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/scenes/tree');
      setTreeData(data);
    } catch { message.error('加载场景数据失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTree(); }, []);

  // 构建树形数据
  const buildTreeData = (): TreeNode[] => {
    const sortedPhases = [...treeData.phases].sort((a, b) => a.sort_order - b.sort_order);
    
    return sortedPhases.map(phase => {
      const phaseGroups = treeData.groups
        .filter(g => g.phase_id === phase.id)
        .sort((a, b) => a.sort_order - b.sort_order);
      
      const groupNodes: TreeNode[] = phaseGroups.map(group => {
        const groupCharts = treeData.charts
          .filter(c => c.group_id === group.id)
          .sort((a, b) => a.sort_order - b.sort_order);
        
        const chartNodes: TreeNode[] = groupCharts.map(chart => ({
          key: `chart-${chart.id}`,
          title: (
            <Space>
              <FileImageOutlined style={{ color: '#722ed1' }} />
              <span>{chart.name}</span>
              <Tag color="purple">{chart.chart_type}</Tag>
            </Space>
          ),
          isLeaf: true,
          data: chart,
          type: 'chart',
        }));

        return {
          key: `group-${group.id}`,
          title: (
            <Space>
              <FolderOutlined style={{ color: '#1677ff' }} />
              <span>{group.name}</span>
              {group.prompt && <Tooltip title="已配置AI提示词"><ThunderboltOutlined style={{ color: '#faad14' }} /></Tooltip>}
            </Space>
          ),
          children: chartNodes,
          data: group,
          type: 'group',
        };
      });

      return {
        key: `phase-${phase.id}`,
        title: (
          <Space>
            <AppstoreOutlined style={{ color: '#52c41a' }} />
            <span style={{ fontWeight: 500 }}>{phase.name}</span>
            <Badge count={phaseGroups.length} style={{ backgroundColor: '#52c41a' }} />
          </Space>
        ),
        children: groupNodes,
        data: phase,
        type: 'phase',
      };
    });
  };

  const handleAdd = (type: 'phase' | 'group' | 'chart', parentId?: number) => {
    form.resetFields();
    setModal({ open: true, type, parentId });
  };

  const handleEdit = (type: 'phase' | 'group' | 'chart', data: any) => {
    form.setFieldsValue({
      ...data,
      prompt: data.prompt || '',
    });
    setModal({ open: true, type, editData: data });
  };

  const handleDelete = async (type: 'phase' | 'group' | 'chart', id: number) => {
    try {
      const urlMap = { phase: '/scenes/phases', group: '/scenes/groups', chart: '/scenes/charts' };
      await api.delete(`${urlMap[type]}/${id}`);
      message.success('删除成功');
      loadTree();
      setSelectedKey(null);
      setSelectedNode(null);
    } catch { message.error('删除失败'); }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      const urlMap = { phase: '/scenes/phases', group: '/scenes/groups', chart: '/scenes/charts' };
      if (modal.editData) {
        await api.put(`${urlMap[modal.type]}/${modal.editData.id}`, values);
      } else {
        const payload = modal.type === 'group' ? { ...values, phase_id: modal.parentId } :
                        modal.type === 'chart' ? { ...values, group_id: modal.parentId } : values;
        await api.post(urlMap[modal.type], payload);
      }
      message.success(modal.editData ? '更新成功' : '创建成功');
      setModal({ open: false, type: 'phase' });
      loadTree();
    } catch { message.error('操作失败'); }
  };

  // AI生成提示词
  const handleAIGenerate = async () => {
    const values = await aiForm.validateFields();
    setAiModal(prev => ({ ...prev, generating: true }));
    try {
      const res = await api.post('/scenes/generate-prompt', {
        description: values.description,
        type: modal.type,
        name: form.getFieldValue('name') || '',
      });
      form.setFieldValue('prompt', res.data.prompt);
      message.success('AI生成提示词成功');
      setAiModal({ open: false, generating: false, description: '' });
    } catch (error: any) {
      message.error(error.response?.data?.error || '生成失败');
    } finally {
      setAiModal(prev => ({ ...prev, generating: false }));
    }
  };

  const onSelect = (keys: React.Key[], info: any) => {
    if (keys.length > 0) {
      setSelectedKey(keys[0] as string);
      setSelectedNode(info.node as TreeNode);
    } else {
      setSelectedKey(null);
      setSelectedNode(null);
    }
  };

  // 渲染选中节点的详情卡片
  const renderDetailCard = () => {
    if (!selectedNode) {
      return (
        <Card>
          <Empty description="请选择左侧的阶段、分组或图表查看详情" />
        </Card>
      );
    }

    const { type, data } = selectedNode;
    const isPhase = type === 'phase';
    const isGroup = type === 'group';
    const isChart = type === 'chart';

    return (
      <Card
        title={
          <Space>
            {isPhase && <AppstoreOutlined style={{ color: '#52c41a' }} />}
            {isGroup && <FolderOutlined style={{ color: '#1677ff' }} />}
            {isChart && <FileImageOutlined style={{ color: '#722ed1' }} />}
            <span>{data?.name}</span>
            {isChart && <Tag color="purple">{(data as Chart)?.chart_type}</Tag>}
          </Space>
        }
        extra={
          <Space>
            <Button icon={<EditOutlined />} onClick={() => handleEdit(type, data)}>编辑</Button>
            <Popconfirm title="确认删除？" onConfirm={() => data?.id && handleDelete(type, data.id)}>
              <Button danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {data?.description && (
            <div>
              <Text type="secondary">描述</Text>
              <Paragraph>{data.description}</Paragraph>
            </div>
          )}
          
          {(isGroup || isChart) && (
            <div>
              <Space>
                <Text type="secondary">AI提示词</Text>
                {(data as Group | Chart)?.prompt && <ThunderboltOutlined style={{ color: '#faad14' }} />}
              </Space>
              {(data as Group | Chart)?.prompt ? (
                <Card size="small" style={{ marginTop: 8, background: '#f6ffed', borderColor: '#b7eb8f' }}>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{(data as Group | Chart).prompt}</Paragraph>
                </Card>
              ) : (
                <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>未配置AI提示词</Text>
              )}
            </div>
          )}

          {isPhase && (
            <div>
              <Text type="secondary">包含分组</Text>
              <div style={{ marginTop: 8 }}>
                {treeData.groups.filter(g => g.phase_id === data?.id).map(g => (
                  <Tag key={g.id} style={{ marginBottom: 4 }}>{g.name}</Tag>
                ))}
              </div>
            </div>
          )}

          {isGroup && (
            <div>
              <Text type="secondary">包含图表</Text>
              <div style={{ marginTop: 8 }}>
                {treeData.charts.filter(c => c.group_id === data?.id).map(c => (
                  <Tag key={c.id} color="purple" style={{ marginBottom: 4 }}>{c.name}</Tag>
                ))}
              </div>
            </div>
          )}

          <Divider />
          
          <Row gutter={16}>
            <Col span={8}>
              <Text type="secondary">ID</Text>
              <div>{data?.id}</div>
            </Col>
            <Col span={8}>
              <Text type="secondary">排序</Text>
              <div>{data?.sort_order}</div>
            </Col>
            {isChart && (
              <Col span={8}>
                <Text type="secondary">图表类型</Text>
                <div>{(data as Chart)?.chart_type}</div>
              </Col>
            )}
          </Row>
        </Space>
      </Card>
    );
  };

  const modalTitle = `${modal.editData ? '编辑' : '新增'}${modal.type === 'phase' ? '阶段' : modal.type === 'group' ? '分组' : '图表'}`;

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>场景管理</Title>
      <Row gutter={24}>
        {/* 左侧树形结构 */}
        <Col span={8}>
          <Card
            title={
              <Space>
                <span>场景结构</span>
                <Badge count={treeData.phases.length} showZero style={{ backgroundColor: '#52c41a' }} />
              </Space>
            }
            extra={
              <Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => handleAdd('phase')}>
                新增阶段
              </Button>
            }
          >
            <Spin spinning={loading}>
              <Tree
                treeData={buildTreeData()}
                selectedKeys={selectedKey ? [selectedKey] : []}
                onSelect={onSelect}
                defaultExpandAll
                blockNode
              />
            </Spin>
            <Divider />
            <Button icon={<ReloadOutlined />} block onClick={loadTree}>刷新数据</Button>
          </Card>
        </Col>

        {/* 右侧详情/操作区 */}
        <Col span={16}>
          {selectedNode?.type === 'group' && (
            <Card style={{ marginBottom: 16 }}>
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAdd('chart', selectedNode.data?.id)}>
                  添加图表
                </Button>
                <Text type="secondary">在当前分组下添加图表</Text>
              </Space>
            </Card>
          )}
          
          {selectedNode?.type === 'phase' && (
            <Card style={{ marginBottom: 16 }}>
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAdd('group', selectedNode.data?.id)}>
                  添加分组
                </Button>
                <Text type="secondary">在当前阶段下添加分组</Text>
              </Space>
            </Card>
          )}

          {renderDetailCard()}
        </Col>
      </Row>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={modalTitle}
        open={modal.open}
        onCancel={() => setModal({ open: false, type: 'phase' })}
        onOk={handleSubmit}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入名称" />
          </Form.Item>
          
          {modal.type === 'chart' && (
            <Form.Item name="chart_type" label="图表类型" rules={[{ required: true, message: '请选择图表类型' }]}>
              <Select placeholder="请选择图表类型">
                <Select.Option value="competitive-matrix">竞品矩阵</Select.Option>
                <Select.Option value="competitive-position">竞争定位图</Select.Option>
                <Select.Option value="competitive-radar">雷达图</Select.Option>
                <Select.Option value="competitive-swot">SWOT分析</Select.Option>
                <Select.Option value="data-abtest">A/B测试</Select.Option>
                <Select.Option value="data-flow">用户流程图</Select.Option>
                <Select.Option value="user-persona">用户画像</Select.Option>
                <Select.Option value="feature-priority">功能优先级</Select.Option>
                <Select.Option value="ui-mockup">UI原型图</Select.Option>
                <Select.Option value="other">其他</Select.Option>
              </Select>
            </Form.Item>
          )}
          
          <Form.Item name="description" label="描述">
            <TextArea rows={2} placeholder="请输入描述（可选）" />
          </Form.Item>
          
          {(modal.type === 'group' || modal.type === 'chart') && (
            <Form.Item name="prompt" label="AI提示词">
              <TextArea
                rows={6}
                placeholder="请输入AI生成提示词，或使用下方按钮自动生成"
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <Button
                type="dashed"
                icon={<RobotOutlined />}
                onClick={() => {
                  aiForm.setFieldsValue({ description: form.getFieldValue('description') || '' });
                  setAiModal({ open: true, generating: false, description: '' });
                }}
                style={{ marginTop: 8 }}
              >
                AI生成提示词
              </Button>
            </Form.Item>
          )}
          
          <Form.Item name="sort_order" label="排序" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小排序越靠前" />
          </Form.Item>
        </Form>
      </Modal>

      {/* AI生成提示词弹窗 */}
      <Modal
        title="AI生成提示词"
        open={aiModal.open}
        onCancel={() => setAiModal({ open: false, generating: false, description: '' })}
        onOk={handleAIGenerate}
        confirmLoading={aiModal.generating}
        okText="生成"
        cancelText="取消"
      >
        <Form form={aiForm} layout="vertical">
          <Form.Item
            name="description"
            label="描述需求"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <TextArea
              rows={4}
              placeholder="请描述您需要的提示词用途，例如：生成一个竞品分析的专业提示词，用于分析SaaS产品的功能对比"
            />
          </Form.Item>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            AI将根据您的描述生成专业的提示词，用于指导图表生成。
          </Paragraph>
        </Form>
      </Modal>
    </div>
  );
}
